import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {
  computeCanDeleteTable,
  computeCanRenameTable,
} from 'notebook/tables/store/logic/TableModificationFlagsLogic';
import {
  createTableActiveCellSelector,
  TableActiveCellModel,
} from 'notebook/tables/store/selectors/tableModel/TableActiveCellSelectors';
import {
  createTableAffixedSelector,
  TableAffixedModel,
} from 'notebook/tables/store/selectors/tableModel/TableAffixedSelectors';
import {
  createTableColumnHeaderSelector,
  TableColumnHeaderModel,
} from 'notebook/tables/store/selectors/tableModel/TableColumnHeaderSelectors';
import {
  createTableContextSelector,
  TableContextModel,
} from 'notebook/tables/store/selectors/tableModel/TableContextSelectors';
import {
  createTableDimensionsSelector,
  TableDimensionsModel,
} from 'notebook/tables/store/selectors/tableModel/TableDimensionsSelectors';
import {
  createTableFillDownSelector,
  TableFillDownModel,
} from 'notebook/tables/store/selectors/tableModel/TableFillDownSelectors';
import {
  createTableLockingSelector,
  TableLockingModel,
} from 'notebook/tables/store/selectors/tableModel/TableLockingSelectors';
import {
  createTableMeasurementsSelector,
  TableMeasurementsModel,
} from 'notebook/tables/store/selectors/tableModel/TableMeasurementsSelectors';
import {
  createTableMergingSelector,
  TableMergingModel,
} from 'notebook/tables/store/selectors/tableModel/TableMergingSelectors';
import {
  createTableReorderingSelector,
  TableReorderingModel,
} from 'notebook/tables/store/selectors/tableModel/TableReorderingSelectors';
import {
  createTableSelectionSelector,
  TableSelectionModel,
} from 'notebook/tables/store/selectors/tableModel/TableSelectionSelectors';
import {
  createBoundaryIndexSelector,
  createTableSingleInputsSelector,
  TableSingleInputsModel,
} from 'notebook/tables/store/selectors/tableModel/TableSingleInputsSelectors';
import {
  createTableViewsSelector,
  TableViewsModel,
} from 'notebook/tables/store/selectors/tableModel/TableViewsSelectors';
import {
  createTableVisibilitySelector,
  TableVisibilityModel,
} from 'notebook/tables/store/selectors/tableModel/TableVisibilitySelectors';
import {
  createDebuggableSelector,
  createDeepSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableVolatileProps, TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * This model is what is passed down to the components, as well as to the RowModel.
 * This can contain data that is derived from the TableEditableStore.
 *
 * Ideally, logic from TableEditable should move into selectors and those selectors should generate values
 * that are stored on this object.
 */
export interface TableModel {
  props: TableEditableProps;
  state: TableEditableState;
  selection: TableSelectionModel;
  context: TableContextModel;
  dimensions: TableDimensionsModel;
  singleInputs: TableSingleInputsModel;
  locking: TableLockingModel;
  views: TableViewsModel;
  fillDown: TableFillDownModel;
  merging: TableMergingModel;
  activeCell: TableActiveCellModel;
  visibility: TableVisibilityModel;
  measurements: TableMeasurementsModel;
  reordering: TableReorderingModel;
  affixed: TableAffixedModel;
  columnHeader: TableColumnHeaderModel;

  /**
   * Whether the table can be deleted (a delete handler is available).
   */
  canDeleteTable: boolean;

  /**
   * Whether the table can be renamed.
   */
  canRenameTable: boolean;
}

export function createVolatileTablePropsSelector(): (
  store: TableEditableStore
) => TableEditableVolatileProps {
  return createDeepSelector.withTypes<TableEditableStore>()(
    [(store) => store.volatileProps],
    (volatileProps): TableEditableVolatileProps => ({...volatileProps})
  );
}

export function createTableModelSelector(): (store: TableEditableStore) => TableModel {
  const volatileSelector = createVolatileTablePropsSelector();
  const tableContextSelector = createTableContextSelector(volatileSelector);
  const boundaryIndexSelector = createBoundaryIndexSelector(volatileSelector);
  const tableSelectionSelector = createTableSelectionSelector(volatileSelector, boundaryIndexSelector);
  const tableViewsSelector = createTableViewsSelector(volatileSelector, tableContextSelector);
  const tableDimensionsSelector = createTableDimensionsSelector(
    volatileSelector,
    tableContextSelector,
    tableSelectionSelector,
    tableViewsSelector
  );
  const tableLockingSelector = createTableLockingSelector(volatileSelector, tableSelectionSelector);
  const tableFillDownSelector = createTableFillDownSelector(
    tableSelectionSelector,
    volatileSelector,
    tableLockingSelector
  );
  const tableMergingSelector = createTableMergingSelector();
  const tableActiveCellSelector = createTableActiveCellSelector(
    volatileSelector,
    tableSelectionSelector,
    boundaryIndexSelector
  );
  const tableVisibilitySelector = createTableVisibilitySelector(tableSelectionSelector);
  const tableSingleInputsSelector = createTableSingleInputsSelector(
    volatileSelector,
    boundaryIndexSelector,
    tableDimensionsSelector,
    tableVisibilitySelector
  );
  const tableMeasurementsSelector = createTableMeasurementsSelector(
    tableViewsSelector,
    tableDimensionsSelector,
    boundaryIndexSelector
  );
  const tableReorderingSelector = createTableReorderingSelector(tableDimensionsSelector);
  const tableAffixedSelector = createTableAffixedSelector();
  const tableColumnHeaderSelector = createTableColumnHeaderSelector(
    tableContextSelector,
    tableDimensionsSelector
  );

  return createDebuggableSelector.withTypes<TableEditableStore>()(
    [
      volatileSelector,
      (store) => store.stableProps,
      (store) => store.state,
      tableSelectionSelector,
      tableContextSelector,
      tableDimensionsSelector,
      tableSingleInputsSelector,
      tableLockingSelector,
      tableViewsSelector,
      tableFillDownSelector,
      tableMergingSelector,
      tableActiveCellSelector,
      tableVisibilitySelector,
      tableMeasurementsSelector,
      tableReorderingSelector,
      tableAffixedSelector,
      tableColumnHeaderSelector,
      (store) => computeCanDeleteTable(store.stableProps.deleteHandler),
      (store) =>
        computeCanRenameTable(
          store.volatileProps.table._hideRename ?? false,
          store.stableProps.canRename ?? true
        ),
    ],
    (
      volatileProps,
      props,
      state,
      selection,
      context,
      dimensions,
      singleInputs,
      locking,
      views,
      fillDown,
      merging,
      activeCell,
      visibility,
      measurements,
      reordering,
      affixed,
      columnHeader,
      canDeleteTable,
      canRenameTable
    ): TableModel => ({
      props: {
        ...props,
        ...volatileProps,
      },
      state,
      selection,
      context,
      dimensions,
      singleInputs,
      locking,
      views,
      fillDown,
      merging,
      activeCell,
      visibility,
      measurements,
      reordering,
      affixed,
      columnHeader,
      canDeleteTable,
      canRenameTable,
    })
  );
}
